import { IconButton, makeStyles } from "@material-ui/core";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { InsCommentList_comments_edges_node } from "@saleor/inscomment/types/InsCommentList";
import { InsCommentListUrlSortField } from "@saleor/inscomment/urls";
import { maybe, renderCollection } from "@saleor/misc";
import { ListActions, ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import moment from "moment-timezone";
import React from "react";
import { FormattedMessage } from "react-intl";

export interface InsCommentListProps
  extends ListProps,
    ListActions,
    SortPage<InsCommentListUrlSortField> {
  comments: InsCommentList_comments_edges_node[];
  onVisibleUpdate: (id: string) => void;
}
const numberOfColumns = 5;

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      apply: {
        width: 80
      },
      colName: {
        width: 250,
        textAlign: "left",
        overflow: "hidden", // 超出的文本隐藏
        textOverflow: "ellipsis", // 溢出用省略号显示
        whiteSpace: "nowrap" // 溢出不换行
      },
      colText: {
        textAlign: "left",
        width: "auto",
        overflow: "hidden", // 超出的文本隐藏
        textOverflow: "ellipsis", // 溢出用省略号显示
        whiteSpace: "nowrap" // 溢出不换行
      },
      colSources: {
        width: 150,
        textAlign: "center"
      },
      colIsVisible: {
        width: 150,
        textAlign: "center"
      },
      colTime: {
        width: 160,
        textAlign: "center"
      }
    },
    apply: {
      color: "red"
    }
  }),
  { name: "CurrencyrateList" }
);
const InsCommentList: React.FC<InsCommentListProps> = ({
  comments,
  disabled,
  isChecked,
  onNextPage,
  onPreviousPage,
  onRowClick,
  pageInfo,
  selected,
  sort,
  toggle,
  toggleAll,
  toolbar,
  onSort,
  settings,
  onVisibleUpdate,
  onUpdateListSettings
}) => {
  const classes = useStyles();
  // const intl = useIntl();

  return (
    <ResponsiveTable>
      <TableHead
        colSpan={numberOfColumns}
        selected={selected}
        disabled={disabled}
        items={comments}
        toggleAll={toggleAll}
        toolbar={toolbar}
      >
        <TableCellHeader
          direction={
            sort.sort === InsCommentListUrlSortField.name
              ? getArrowDirection(sort.asc)
              : undefined
          }
          className={classes.colName}
          onClick={() => onSort(InsCommentListUrlSortField.name)}
        >
          <FormattedMessage defaultMessage="User Name" />
        </TableCellHeader>
        <TableCellHeader className={classes.colText}>
          <FormattedMessage defaultMessage="Comment" />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === InsCommentListUrlSortField.created
              ? getArrowDirection(sort.asc)
              : undefined
          }
          className={classes.colTime}
          onClick={() => onSort(InsCommentListUrlSortField.created)}
        >
          <FormattedMessage defaultMessage="Created Time" />
        </TableCellHeader>
        <TableCellHeader className={classes.colSources}>
          <FormattedMessage defaultMessage="Sources" />
        </TableCellHeader>
        <TableCellHeader className={classes.colIsVisible}>
          <FormattedMessage defaultMessage="is Visible" />
        </TableCellHeader>
      </TableHead>

      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            settings={settings}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>

      <TableBody>
        {renderCollection(
          comments,
          comment => {
            const isSelected = comment ? isChecked(comment.id) : false;
            return (
              <TableRow
                hover={!!comment}
                onClick={comment && onRowClick(comment.id)}
                key={comment ? comment.id : "skeleton"}
                selected={isSelected}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    disabled={disabled}
                    disableClickPropagation
                    onChange={() => toggle(comment.id)}
                  />
                </TableCell>
                <TableCell className={classes.colName} data-test="id">
                  {maybe<React.ReactNode>(() => comment.userName, <Skeleton />)}
                </TableCell>
                <TableCell className={classes.colText} data-test="id">
                  {maybe<React.ReactNode>(() => comment.text, <Skeleton />)}
                </TableCell>
                <TableCell className={classes.colTime}>
                  {comment ? (
                    moment(comment.created).format("YYYY-MM-DD HH:mm:ss")
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colSources} data-test="id">
                  {maybe<React.ReactNode>(() => comment.source, <Skeleton />)}
                </TableCell>
                <TableCell className={classes.colIsVisible} data-test="id">
                  {maybe<React.ReactNode>(
                    () => (
                      <IconButton
                        color="primary"
                        onClick={() => {
                          onVisibleUpdate(comment.id);
                        }}
                      >
                        {comment.isVisible ? (
                          <VisibilityIcon color={"primary"} />
                        ) : (
                          <VisibilityOffIcon color={"inherit"} />
                        )}
                      </IconButton>
                    ),
                    <Skeleton />
                  )}
                </TableCell>
              </TableRow>
            );
          },
          () => (
            <TableRow data-test="emptyListMessage">
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No comment found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};
export default InsCommentList;
